import React, { createContext, useContext, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../components/common/ConfirmDialog';

// Error dialog context
const ErrorDialogContext = createContext(null);

/**
 * Hook: useErrorDialog
 * - Convenience hook to access `showError`/`hideError` from the ErrorDialogContext
 */
export const useErrorDialog = () => useContext(ErrorDialogContext);

/**
 * ErrorDialogProvider
 * - Provides `showError` and `hideError` helpers and renders a confirm dialog used as an error popup.
 */
export const ErrorDialogProvider = ({ children }) => {
  const { t } = useTranslation();
  const [state, setState] = useState({ isOpen: false, message: '', title: t('common.dialogs.confirm.title') });

  /** Show an error dialog with optional title */
  const showError = useCallback((message, title) => {
    setState({ isOpen: true, message: String(message || ''), title: title || t('common.dialogs.confirm.title') });
  }, [t]);

  /** Hide the currently shown error dialog */
  const hideError = useCallback(() => {
    setState({ isOpen: false, message: '', title: t('common.dialogs.confirm.title') });
  }, [t]);

  return (
    <ErrorDialogContext.Provider value={{ showError, hideError }}>
      {children}
      <ConfirmDialog
        isOpen={state.isOpen}
        title={state.title}
        message={state.message}
        confirmLabel={t('common.buttons.ok')}
        cancelLabel={null}
        onConfirm={hideError}
        onCancel={hideError}
        confirmVariant="primary"
        loading={false}
      />
    </ErrorDialogContext.Provider>
  );
};

export default ErrorDialogContext;
